import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { doc, getDoc, collection, query, where, orderBy, onSnapshot, documentId } from "firebase/firestore";
import { db } from "../firebase/config";
import type { SymptomKey, RecoveryDailyLog } from "../types/recovery";
import { symptomConfigs } from "../config/symptomsConfig";
import {
  computeDailyRisk,
  formatWeekdayLetter,
  getLastNDateIds,
  type SymptomDaySummary,
} from "../utils/symptomUtils";

type LoadState = "idle" | "loading" | "ready" | "error";

type SymptomStats = {
  trackedDays: number;
  alertDays: number;
  monitorDays: number;
  okDays: number;
  topSymptomLabels: string[];
};

type UseSymptomLogsReturn = {
  days: SymptomDaySummary[];
  loadState: LoadState;
  errorMessage: string | null;
  stats: SymptomStats;
  description: string;
  todaySummary: string | null;
  refetch: () => void;
};

// ─── Postpartum Bleeding Helpers ──────────────────────────────────────────────

type FlowTrend = "decreasing" | "increasing" | "stable" | "unknown";
type ColourTrend = "lightening" | "darkening" | "stable" | "unknown";
type LochiaStage = 1 | 2 | 3;

const FLOW_RANK: Record<string, number> = { heavy: 3, moderate: 2, light: 1, mild: 1, none: 0 };
const COLOUR_STAGE: Record<string, number> = {
  bright_red: 1, dark_red: 1, pinkish_brown: 2,
  light_pink: 2, pink: 2, brown: 2, white: 3, clear: 3,
};

const LOCHIA_NAMES: Record<LochiaStage, string> = {
  1: "1st stage of postpartum bleeding (Lochia rubra)",
  2: "2nd stage of postpartum bleeding (Lochia serosa)",
  3: "3rd stage of postpartum bleeding (Lochia alba)",
};

function getDaysPostpartum(deliveryDate: string): number {
  const delivery = new Date(deliveryDate + "T00:00:00");
  if (isNaN(delivery.getTime())) return 0;
  return Math.floor((Date.now() - delivery.getTime()) / (1000 * 60 * 60 * 24));
}

function detectLochiaStage(daysPostpartum: number): LochiaStage {
  if (daysPostpartum <= 4) return 1;
  if (daysPostpartum <= 10) return 2;
  return 3;
}

// Extracts a numeric flow value from a day, returns null if no bleeding logged
function extractFlowValue(d: SymptomDaySummary): number | null {
  if (!d.log) return null;
  const vb = d.log?.symptoms?.vaginalBleeding;
  if (!vb) return null;
  if (
    vb.overallRisk?.toLowerCase().trim() === "ok" ||
    vb.overallRisk?.toLowerCase().trim() === "normal" ||
    vb.flow?.toLowerCase().trim() === "none"
  ) return 0;
  
  const normalizedFlow = vb.flow?.toLowerCase().trim();
  return normalizedFlow ? (FLOW_RANK[normalizedFlow] ?? null) : null;
}

// Extracts a numeric colour value from a day, returns null if no bleeding logged
function extractColourValue(d: SymptomDaySummary): number | null {
  if (!d.log) return null;
  const vb = d.log?.symptoms?.vaginalBleeding;
  if (!vb) return null;
  if (
    vb.overallRisk?.toLowerCase().trim() === "ok" ||
    vb.overallRisk?.toLowerCase().trim() === "normal" ||
    vb.flow?.toLowerCase().trim() === "none"
  ) return 3;

  const normalizedColour = vb.colour?.toLowerCase().trim().replace(/\s+/g, "_");
  return normalizedColour ? (COLOUR_STAGE[normalizedColour] ?? null) : null;
}

// Compares FIRST logged entry vs LAST logged entry across full journey
function detectFlowTrend(allDays: SymptomDaySummary[]): FlowTrend {
  const values = allDays
    .map(extractFlowValue)
    .filter((v): v is number => v !== null);
  if (values.length < 2) return "unknown";
  const first = values[0];
  const last = values[values.length - 1];
  return last < first ? "decreasing" : last > first ? "increasing" : "stable";
}

// Compares FIRST logged entry vs LAST logged entry across full journey
function detectColourTrend(allDays: SymptomDaySummary[]): ColourTrend {
  const values = allDays
    .map(extractColourValue)
    .filter((v): v is number => v !== null);
  if (values.length < 2) return "unknown";
  const first = values[0];
  const last = values[values.length - 1];
  return last > first ? "lightening" : last < first ? "darkening" : "stable";
}

// Checks last 3 days for a sudden spike — independent of overall trend
function detectAnomaly(recentDays: SymptomDaySummary[]): boolean {
  const last3 = recentDays.slice(-3);
  const values = last3
    .map(extractFlowValue)
    .filter((v): v is number => v !== null);
  if (values.length < 2) return false;
  // Anomaly = flow increased in the last 3 days and last value is heavy or moderate
  const isIncreasing = values[values.length - 1] > values[0];
  const isConcerning = values[values.length - 1] >= 2; // moderate or heavy
  return isIncreasing && isConcerning;
}

function buildLochiaMessage(
  daysPostpartum: number,
  flowTrend: FlowTrend,
  colourTrend: ColourTrend,
  currentStage: LochiaStage,
  hasAnomaly: boolean
): string {
  const current = LOCHIA_NAMES[currentStage];
  const next = currentStage < 3 ? LOCHIA_NAMES[(currentStage + 1) as LochiaStage] : null;

  // Anomaly warning appended to any message when a recent spike is detected
  const anomalyWarning = hasAnomaly
    ? " However, your flow has increased in the last couple of days — keep an eye on it and consider reaching out to your healthcare provider if it continues."
    : "";

  // 1. Decreasing Flow Combinations
  if (flowTrend === "decreasing" && colourTrend === "lightening")
    return next
      ? `Since you started logging, your flow has been reducing and your colour has been changing — you're probably moving to the ${next}.${anomalyWarning}`
      : `Since you started logging, your flow has been reducing and your colour has been lightening. You're in the final stage of postpartum bleeding — great progress!${anomalyWarning}`;

  if (flowTrend === "decreasing" && colourTrend === "stable")
    return `Since you started logging, your flow has been reducing — your colour hasn't changed much yet, but that's normal. You're currently in the ${current}.${anomalyWarning}`;

  if (flowTrend === "decreasing" && colourTrend === "darkening")
    return `Since you started logging, your flow has been reducing but your colour has been getting darker. You're in the ${current} — keep logging and mention this at your next check-up.${anomalyWarning}`;

  if (flowTrend === "decreasing" && colourTrend === "unknown")
    return `Since you started logging, your bleeding flow has been reducing. Keep tracking your bleeding colour daily to get a complete pattern analysis.${anomalyWarning}`;

  // 2. Increasing Flow Combinations
  if (flowTrend === "increasing" && colourTrend === "lightening")
    return `Since you started logging, your colour has been lightening but your flow has increased overall. You're in the ${current} — keep an eye on it.${anomalyWarning}`;

  if (flowTrend === "increasing" && colourTrend === "stable")
    return `Since you started logging, your flow has been increasing. You're in the ${current} — if this continues, consider reaching out to your healthcare provider.${anomalyWarning}`;

  if (flowTrend === "increasing" && colourTrend === "darkening")
    return `Since you started logging, your flow has been increasing and your colour has been getting darker. Please consider reaching out to your healthcare provider soon.${anomalyWarning}`;

  if (flowTrend === "increasing" && colourTrend === "unknown")
    return `Since you started logging, your bleeding flow has been increasing overall. Keep close track of your updates and log your bleeding colour daily.${anomalyWarning}`;

  // 3. Stable Flow Combinations
  if (flowTrend === "stable" && colourTrend === "lightening")
    return next
      ? `Since you started logging, your flow has been steady and your colour has been lightening — you're probably approaching the ${next} soon.${anomalyWarning}`
      : `Since you started logging, your flow has been steady and your colour has been lightening. You're almost through the final stage — keep it up!${anomalyWarning}`;

  if (flowTrend === "stable" && colourTrend === "stable")
    return `Your bleeding has been consistent since you started logging. You're currently in the ${current}, day ${daysPostpartum} postpartum — keep checking in daily.${anomalyWarning}`;

  if (flowTrend === "stable" && colourTrend === "darkening")
    return `Since you started logging, your flow has been steady but your colour has been getting darker. You're in the ${current} — worth mentioning at your next check-up.${anomalyWarning}`;

  if (flowTrend === "stable" && colourTrend === "unknown")
    return `Your bleeding flow has been consistent since you started logging. Remember to track your color selections too to see your full progression.${anomalyWarning}`;

  // 4. Unknown Flow Combinations
  if (flowTrend === "unknown" && colourTrend === "lightening")
    return `Your bleeding colour has been lightening — a positive sign. Log your flow daily too so we can track your full progression.`;

  if (flowTrend === "unknown" && colourTrend === "darkening")
    return `Your bleeding colour has been getting darker. Log your flow daily too, and keep an eye on how you're feeling.`;

  if (flowTrend === "unknown" && colourTrend === "stable")
    return `Your bleeding colour has been consistent across your logged entries. Make sure to track your flow properties daily to unlock more feedback.`;

  // 5. Catch-All for Logs Present with Unknown/Mixed State Triggers
  return `You are currently on day ${daysPostpartum} postpartum (${current}). Continue tracking your daily bleeding flow and colour characteristics to see your personalised recovery analysis here.${anomalyWarning}`;
}

const resolveSymptomLabel = (key: SymptomKey): string => {
  if (key === "vaginalBleeding") return "bleeding";
  const cfg = symptomConfigs[key];
  return cfg?.label?.replace(" or discomfort", "").replace(" / leaks", "").replace(" / sleep quality", "") ?? key;
};

const joinParts = (parts: string[]): string => {
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts[0]}, ${parts[1]} and ${parts[2]}`;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;

export const useSymptomLogs = (userId: string): UseSymptomLogsReturn => {
  const [loadState, setLoadState] = useState<LoadState>("idle");
  const [days, setDays] = useState<SymptomDaySummary[]>([]); // chart: last 7 days only
  const [allBleedingDays, setAllBleedingDays] = useState<SymptomDaySummary[]>([]); // trend: full journey
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null);

  const fetchDeliveryDate = useRef<() => Promise<void>>(async () => {});

  // ── Fetch delivery date ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;

    const fetch = async () => {
      try {
        const [a1Snap, b1Snap] = await Promise.all([
          getDoc(doc(db, "questionnaires", userId, "responses", "FTU.A.1")),
          getDoc(doc(db, "questionnaires", userId, "responses", "FTU.B.1")),
        ]);
        if (a1Snap.exists()) setDeliveryDate(a1Snap.data()?.answer ?? null);
        else if (b1Snap.exists()) setDeliveryDate(b1Snap.data()?.answer ?? null);
      } catch (e) {
        console.error("Delivery date fetch error:", e);
      }
    };

    fetchDeliveryDate.current = fetch;
    fetch();

    const intervalId = setInterval(() => fetchDeliveryDate.current(), POLL_INTERVAL_MS);
    const onFocus = () => fetchDeliveryDate.current();
    const onVisible = () => { if (document.visibilityState === "visible") fetchDeliveryDate.current(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [userId]);

  // ── Query 1: last 7 days for the dashboard chart ─────────────────────────────
  useEffect(() => {
    if (!userId) return;

    setLoadState("loading");
    setErrorMessage(null);

    const dateIds = getLastNDateIds(7);
    const logsRef = collection(db, "users", userId, "recoveryDailyLogs");
    const logsQuery = query(logsRef, where(documentId(), "in", dateIds));

    const unsubscribe = onSnapshot(
      logsQuery,
      (snapshot) => {
        const logsMap: Record<string, RecoveryDailyLog> = {};
        snapshot.forEach((doc) => { logsMap[doc.id] = doc.data() as RecoveryDailyLog; });

        const results: SymptomDaySummary[] = dateIds.map((dateId) => {
          const data = logsMap[dateId];
          if (data) {
            return {
              dateId,
              weekdayLabel: formatWeekdayLetter(dateId),
              risk: computeDailyRisk(data),
              alertSymptoms: (data.symptomAlerts?.alertSymptoms ?? []) as SymptomKey[],
              log: data,
            };
          }
          return { dateId, weekdayLabel: formatWeekdayLetter(dateId), risk: "none" as const, alertSymptoms: [] };
        });

        setDays(results);
        setLoadState("ready");
      },
      (error) => {
        console.error("Snapshot error:", error);
        setErrorMessage("We could not load your symptoms right now.");
        setLoadState("error");
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // ── Query 2: all logs since delivery date for trend calculations ──────────────
  useEffect(() => {
    if (!userId || !deliveryDate) return;

    const logsRef = collection(db, "users", userId, "recoveryDailyLogs");
    const allLogsQuery = query(
      logsRef,
      where(documentId(), ">=", deliveryDate),
      orderBy(documentId(), "asc")
    );

    const unsubscribe = onSnapshot(allLogsQuery, (snapshot) => {
      const results: SymptomDaySummary[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as RecoveryDailyLog;
        if (data.symptoms?.vaginalBleeding) {
          results.push({
            dateId: doc.id,
            weekdayLabel: formatWeekdayLetter(doc.id),
            risk: computeDailyRisk(data),
            alertSymptoms: (data.symptomAlerts?.alertSymptoms ?? []) as SymptomKey[],
            log: data,
          });
        }
      });
      setAllBleedingDays(results);
    });

    return () => unsubscribe();
  }, [userId, deliveryDate]);

  const refetch = useCallback(() => {
    fetchDeliveryDate.current();
  }, []);

  // ── Stats: based on last 7 days (chart data) ──────────────────────────────────
  const stats = useMemo((): SymptomStats => {
    if (days.length === 0)
      return { trackedDays: 0, alertDays: 0, monitorDays: 0, okDays: 0, topSymptomLabels: [] };

    const trackedDays = days.filter((d) => d.risk !== "none").length;
    const alertDays   = days.filter((d) => d.risk === "alert").length;
    const monitorDays = days.filter((d) => d.risk === "monitor").length;
    const okDays      = days.filter((d) => d.risk === "ok").length;

    const symptomCount: Partial<Record<SymptomKey, number>> = {};
    days.forEach((d) => d.alertSymptoms.forEach((key) => {
      symptomCount[key] = (symptomCount[key] ?? 0) + 1;
    }));

    const topSymptomLabels = (Object.entries(symptomCount) as [SymptomKey, number][])
      .filter(([, count]) => count > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([key]) => resolveSymptomLabel(key));

    return { trackedDays, alertDays, monitorDays, okDays, topSymptomLabels };
  }, [days]);

  // ── Description: based on last 7 days ────────────────────────────────────────
  const description = useMemo((): string => {
    if (loadState === "loading") return "Loading your symptoms…";
    const { trackedDays, alertDays, topSymptomLabels } = stats;
    if (trackedDays === 0)
      return "You haven't logged any symptoms in the last 7 days. Start tracking to see your summary here.";
    const attentionPhrase =
      alertDays === 0 ? "no days where symptoms needed attention"
      : alertDays === 1 ? "1 day where a symptom needs attention"
      : `${alertDays} days where a symptom needs attention`;
    const themesPhrase = topSymptomLabels.length > 0 ? ` The main themes were ${joinParts(topSymptomLabels)}.` : "";
    return `In the last 7 days, you had ${attentionPhrase}.${themesPhrase}`;
  }, [loadState, stats]);

  // ── Today summary: based on full journey + anomaly check on last 3 days ───────
  const todaySummary = useMemo((): string | null => {
    if (loadState !== "ready") return null;
    if (!deliveryDate) return null;

    if (allBleedingDays.length === 0)
      return "Log your bleeding each day to get a personalised update on your postpartum bleeding summary.";

    const daysPostpartum = getDaysPostpartum(deliveryDate);
    const flowTrend = detectFlowTrend(allBleedingDays);       
    const colourTrend = detectColourTrend(allBleedingDays);   
    const hasAnomaly = detectAnomaly(days);                   

    return buildLochiaMessage(daysPostpartum, flowTrend, colourTrend, detectLochiaStage(daysPostpartum), hasAnomaly);
  }, [loadState, allBleedingDays, days, deliveryDate]);

  return { days, loadState, errorMessage, stats, description, todaySummary, refetch };
};